<template>
  <tr
    class="task-row-component"
    v-bind:class="{ selected: selected }"
    v-on:click.stop="onSelectionChanged"
  >
    <td v-if="computedIsSelectable" class="compact half-padding">
      <a-checkbox
        ref="checkbox"
        :checked="selected"
        @change="onSelectionChanged"
        v-on:click.stop
      ></a-checkbox>
    </td>
    <td>
      <div :style="{ color: priorityColor }" v-if="!editing">
        <div style="padding-top: 0.5rem">
          <a-popover
            v-if="hasDescription"
            placement="topLeft"
            title="Omschrijving"
          >
            <template slot="content">
              <div style="max-width: 300px">
                {{ task.description }}
              </div>
            </template>
            <phone-return-icon v-if="task.type === 1"></phone-return-icon>
            <magnify-icon v-else-if="task.type === 2"></magnify-icon>
            <email-icon v-else-if="task.type === 3"></email-icon>
            <cake-variant-icon v-else-if="task.type === 4"></cake-variant-icon>
            <calendar-check-icon v-else></calendar-check-icon>
            {{ task.content }}
          </a-popover>
          <span v-if="!hasDescription">
            <phone-return-icon v-if="task.type === 1"></phone-return-icon>
            <magnify-icon v-else-if="task.type === 2"></magnify-icon>
            <email-icon v-else-if="task.type === 3"></email-icon>
            <cake-variant-icon v-else-if="task.type === 4"></cake-variant-icon>
            <calendar-check-icon v-else></calendar-check-icon>
            {{ task.content }}
          </span>
          <pencil-icon
            class="edit-name"
            style="cursor: pointer"
            v-on:click.stop="onEditNameClick"
          ></pencil-icon>
          <div
            v-if="hasAttachments"
            style="
              display: inline-block;
              margin-left: 1.5rem;
              margin-top: -0.5rem;
              float: right;
            "
          >
            <a-popover placement="topLeft" title="Omschrijving">
              <template slot="content">
                <div style="max-width: 300px">
                  <div v-for="attachable in attachables" :key="attachable.id">
                    <attachment-icon></attachment-icon>
                    <AttachableShowLinkComponent
                      :attachable="attachable"
                    ></AttachableShowLinkComponent>
                  </div>
                </div>
              </template>
              <a-button type="default" v-on:click.stop="onAttachableDrawerOpen">
                <attachment-icon></attachment-icon>
                {{ attachables.length }} bijlage(s)
              </a-button>
            </a-popover>
            <AttachableDrawerTableComponent
              :attachables="attachables"
              :visible="attachableDrawerVisible"
              v-on:close="onAttachableDrawerClose"
            >
            </AttachableDrawerTableComponent>
          </div>
        </div>
      </div>
      <div v-if="editing">
        <a-input-group>
          <a-input
            ref="taskNameInput"
            :defaultValue="task.content"
            style="width: 80%"
            v-bind:disabled="updateNameLoading"
            v-on:click.stop
            v-on:keyup.enter="onSave"
            v-on:keyup.esc="onCancel"
          ></a-input>
          <a-button :loading="updateNameLoading" v-on:click.stop="onSave">
            <content-save-icon></content-save-icon>
          </a-button>
        </a-input-group>
      </div>
    </td>
    <td v-if="displayProductLabels">
      <span
        v-for="productLabel in productLabels"
        v-if="hasProductLabels"
        :style="{ 'background-color': '#' + productLabel.color }"
        class="label label-default"
      >
        {{ productLabel.tag }}
      </span>
    </td>
    <td v-if="displayTaskableBadge" class="compact">
      <TaskableBadgeComponent
        v-if="hasTaskableInstance"
        :taskable="taskableInstance"
        style="display: inline-block; padding-top: 0.5rem"
      ></TaskableBadgeComponent>
    </td>
    <td v-if="customerIds === null && displayCustomer">
      <CustomerBadgeComponent
        v-if="hasCustomer"
        :customer="customer"
      ></CustomerBadgeComponent>
    </td>
    <td v-if="isCallback" class="compact">
      <CustomerCallButtonComponent
        v-if="hasCustomer"
        :customer="customer"
      ></CustomerCallButtonComponent>
    </td>
    <td class="compact">
      <a-tag :color="dateColor" style="margin-top: 0.4rem">
        {{ date }}
      </a-tag>
    </td>
    <td class="compact">
      <UserBadgeComponent
        v-if="hasCreator"
        :user="task.creator.data"
        size="small"
      >
      </UserBadgeComponent>
      <AutomationBadgeComponent
        v-if="hasAutomation"
        v-bind:automation="automation"
      />
      <GlobalAutomationConfigurationBadgeComponent
        v-if="hasGlobalAutomationConfiguration"
        v-bind:global-automation-configuration="globalAutomationConfiguration"
      />
    </td>
    <td v-if="!displayActiveTasks" class="compact">
      <UserBadgeComponent
        v-if="hasFinisher"
        :key="task.finished_by"
        :user="task.finisher.data"
        size="small"
      ></UserBadgeComponent>
    </td>
    <td v-if="!displayActiveTasks" class="compact">
      <a-tooltip placement="top">
        <template slot="title">
          {{ finishedDateTime }}
        </template>
        <a-tag color="#a465a9" style="margin-top: 0.4rem">
          {{ finishedDate }}
        </a-tag>
      </a-tooltip>
    </td>
    <td v-if="displayActiveTasks" class="compact">
      <TaskBadgeGroupComponent
        :departments="task.departments.data"
        :users="task.users.data"
      ></TaskBadgeGroupComponent>
    </td>
    <td class="compact text-right">
      <i
        v-if="task.visible_for_customer"
        class="mdi mdi-eye tooltip-toggle"
        data-placement="top"
        data-toggle="tooltip"
        style="font-size: 1.5em; color: #6dc287"
        title="Deze taak is zichtbaar voor de klant"
      ></i>

      <i
        v-if="!task.visible_for_customer"
        class="mdi mdi-eye-off tooltip-toggle"
        data-placement="top"
        data-toggle="tooltip"
        style="font-size: 1.5em"
        title="Deze taak is niet zichtbaar voor de klant"
      ></i>
    </td>
    <td class="compact text-right">
      <a-tooltip placement="left" v-if="displayTaskListDrawerButton">
        <template slot="title">
          <span>Behoord tot een takenreeks</span>
        </template>
        <a-button @click="$refs.taskListDrawer.viewTaskList(task.task_list_id)">
          <dd-icon size="sm" icon="mdi mdi-format-list-bulleted"></dd-icon>
        </a-button>
      </a-tooltip>
      <a-tooltip placement="left">
        <template slot="title"> <span>Bewerken</span></template>
        <a-button class="edit-task" v-on:click.stop="onEditClick">
          <pencil-icon></pencil-icon>
        </a-button>
      </a-tooltip>
      <a-popover
        title="Datum aanpassen"
        trigger="click hover"
        placement="leftTop"
      >
        <template slot="content">
          <p>
            <a @click="updateTaskDate(task.id, 0, 'days')">
              <calendar-icon></calendar-icon>
              Vandaag
            </a>
          </p>
          <p>
            <a @click="updateTaskDate(task.id, 1, 'days')">
              <calendar-icon></calendar-icon>
              Morgen
            </a>
          </p>
          <p>
            <a @click="updateTaskDate(task.id, 1, 'weeks')">
              <calendar-icon></calendar-icon>
              Volgende week
            </a>
          </p>
          <p>
            <a @click="updateTaskDate(task.id, 2, 'weeks')">
              <calendar-icon></calendar-icon>
              Over twee weken
            </a>
          </p>
          <p>
            <a @click="updateTaskDate(task.id, 1, 'months')">
              <calendar-icon></calendar-icon>
              Volgende maand
            </a>
          </p>
          <p>
            <a @click="updateTaskDate(task.id, 1, 'years')">
              <calendar-icon></calendar-icon>
              Volgende jaar
            </a>
          </p>
          <hr v-if="canDeleteTask" />
          <p v-if="canDeleteTask">
            <a
              @click="
                () => {
                  deleteModalVisible = true;
                }
              "
              style="color: darkred"
            >
              <delete-icon></delete-icon>
              Verwijderen
            </a>
          </p>
        </template>
        <a-button
          :loading="updateDateLoading"
          class="postpone-task"
          v-if="!isCompleted"
        >
          <dots-vertical-icon></dots-vertical-icon>
        </a-button>

        <delete-modal
          :visible="deleteModalVisible"
          :task="task"
          :description="`Weet je zeker dat je deze taak (${
            task.content || ''
          }) wil verwijderen?`"
          @cancel="
            () => {
              deleteModalVisible = false;
            }
          "
          @deleted="afterDelete"
        ></delete-modal>
      </a-popover>

      <a-popconfirm
        v-if="!isCompleted"
        cancel-text="Annuleren"
        class="finish-task"
        ok-text="Afronden"
        placement="topRight"
        title="Weet je zeker dat je deze taak wil afronden?"
        @confirm="onCompletedClick"
        v-on:click.stop
      >
        <a-tooltip placement="left">
          <template slot="title">
            <span>Markeren als afgerond</span>
          </template>
          <a-button :loading="completeLoading" type="primary" v-on:click.stop>
            <check-icon></check-icon>
          </a-button>
        </a-tooltip>
      </a-popconfirm>
      <a-popconfirm
        v-if="isCompleted"
        cancel-text="Annuleren"
        class="activate-task"
        ok-text="Activeren"
        placement="topRight"
        title="Weet je zeker dat je deze taak weer wil activeren?"
        @confirm="onActivateClick"
        v-on:click.stop
      >
        <a-tooltip placement="left">
          <template slot="title">
            <span>Markeren als actief</span>
          </template>
          <a-button :loading="activateLoading" v-on:click.stop>
            <check-icon></check-icon>
          </a-button>
        </a-tooltip>
      </a-popconfirm>

      <ShowTaskListDrawer ref="taskListDrawer"></ShowTaskListDrawer>
    </td>
  </tr>
</template>
<script>
import {mapGetters} from "vuex";
import taskApi from "~/api/task";
import TaskableBadgeComponent from "~/modules/task/components/TaskableBadgeComponent";
import CustomerBadgeComponent from "~/modules/customer/components/BadgeComponent";
import CustomerCallButtonComponent from "~/modules/customer/components/CallButtonComponent";
import UserBadgeComponent from "~/modules/user/components/BadgeComponent";
import UserDepartmentBadgeComponent from "~/modules/user_department/components/BadgeComponent";
import AttachableShowLinkComponent from "~/modules/attachable/components/ShowLinkComponent";
import TaskBadgeGroupComponent from "./TaskBadgeGroupComponent";
import AttachableDrawerTableComponent from "~/modules/attachable/components/DrawerTableComponent";
import DateFormatEnum from "~/enums/DateFormatEnum";
import AutomationBadgeComponent from "~/pages/automation/components/AutomationBadgeComponent.vue";
import GlobalAutomationConfigurationBadgeComponent
  from "~/pages/automation/components/GlobalAutomationConfigurationBadgeComponent.vue";
import CustomerAvatarCell from "../../../new_design_components/tables/cell_types/CustomerAvatarCell.vue";
import DeleteModal from "../dialog-modal/DeleteModal.vue";
import ShowTaskListDrawer from "~/modules/task_list/components/ShowTaskListDrawer.vue";

const moment = require("moment");

export default {
  name: "TaskRowComponent",
  props: {
    task: {
      type: Object,
      required: true,
    },
    userId: {
      type: Number,
      required: true,
    },
    customerIds: {
      default: null,
    },
    instance: {
      default: null,
    },
    interval: {
      type: Number,
      default: 1,
    },
    isSelectable: {
      type: Boolean,
      default: true,
    },
    displayProductLabels: {
      type: Boolean,
      default: false,
    },
    displayCustomer: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    ShowTaskListDrawer,
    GlobalAutomationConfigurationBadgeComponent,
    AutomationBadgeComponent,
    AttachableDrawerTableComponent,
    TaskBadgeGroupComponent,
    TaskableBadgeComponent,
    CustomerBadgeComponent,
    CustomerCallButtonComponent,
    UserBadgeComponent,
    UserDepartmentBadgeComponent,
    AttachableShowLinkComponent,
    CustomerAvatarCell,
    DeleteModal,
  },
  data() {
    return {
      editing: false,
      updateNameLoading: false,
      completeLoading: false,
      activateLoading: false,
      attachableDrawerVisible: false,
      updateDateLoading: false,
      deleteModalVisible: false,
    };
  },
  computed: {
    priorityColor() {
      if (this.task.priority === 0) {
        return "#ff0000";
      } else if (this.task.priority === 1) {
        return "#ee7600";
      } else if (this.task.priority === 2) {
        return "#000000";
      } else if (this.task.priority === 3) {
        return "#757575";
      } else {
        return "#000000";
      }
    },
    ...mapGetters("task", ["name", "selectedTasks"]),
    taskShowUri() {
      return "/task/show/" + this.task.id;
    },
    selected() {
      return this.selectedTasks.findIndex((x) => x.id === this.task.id) >= 0;
    },
    hasDescription() {
      return this.task.description !== null && this.task.description.length > 0;
    },
    hasCustomer() {
      return (
        typeof this.task.customer_id !== "undefined" &&
        this.task.customer_id !== null &&
        typeof this.task.customer !== "undefined" &&
        this.task.customer !== null &&
        this.task.customer.data !== null
      );
    },
    hasCreator() {
      return (
        typeof this.task.user_id !== "undefined" &&
        this.task.user_id !== null &&
        typeof this.task.creator !== "undefined" &&
        this.task.creator !== null
      );
    },
    automationResult() {
      if (
        this.task.automation_result !== undefined &&
        this.task.automation_result !== null &&
        this.task.automation_result.data !== undefined &&
        this.task.automation_result.data !== null
      ) {
        return this.task.automation_result.data;
      }

      return null;
    },
    automation() {
      if (
        this.automationResult !== undefined &&
        this.automationResult !== null &&
        this.automationResult.automation !== undefined &&
        this.automationResult.automation !== null &&
        this.automationResult.automation.data !== undefined &&
        this.automationResult.automation.data !== null
      ) {
        return this.automationResult.automation.data;
      }

      return null;
    },
    globalAutomationConfiguration() {
      if (
        this.automationResult !== undefined &&
        this.automationResult !== null &&
        this.automationResult.global_automation_configuration !== undefined &&
        this.automationResult.global_automation_configuration !== null &&
        this.automationResult.global_automation_configuration.data !==
          undefined &&
        this.automationResult.global_automation_configuration.data !== null
      ) {
        return this.automationResult.global_automation_configuration.data;
      }

      return null;
    },
    hasAutomation() {
      return this.automation !== null;
    },
    hasGlobalAutomationConfiguration() {
      return this.globalAutomationConfiguration !== null;
    },
    hasFinisher() {
      return (
        typeof this.task.finished_by !== "undefined" &&
        this.task.finished_by !== null &&
        this.task.finisher !== null
      );
    },
    hasAttachments() {
      return (
        typeof this.task.attachables !== "undefined" &&
        this.task.attachables !== null &&
        this.task.attachables.data.length > 0
      );
    },
    attachables() {
      if (this.hasAttachments) {
        return this.task.attachables.data;
      }

      return [];
    },
    date() {
      return moment(this.task.date, moment.ISO_8601).format(
        DateFormatEnum.DISPLAY_DATE
      );
    },
    isCallback() {
      return this.task.type === 1;
    },
    computedIsSelectable() {
      return (
        this.isSelectable !== false &&
        this.customerIds === null &&
        this.instance === null
      );
    },
    isCompleted() {
      return this.task.status === 1;
    },
    customer() {
      return this.task.customer.data;
    },
    customerId() {
      return this.task.customer_id;
    },
    displayTaskableBadge() {
      return this.instance === null;
    },
    hasTaskableInstance() {
      return (
        this.taskableInstance !== null &&
        this.taskableInstance.instance !== "Customer"
      );
    },
    taskableInstance() {
      if (!this.task.taskable) {
        return null;
      }
      return this.task.taskable.data;
    },
    finishedDateTime() {
      return moment(this.task.finished_at, moment.ISO_8601).format(
        DateFormatEnum.DISPLAY_DATETIME
      );
    },
    finishedDate() {
      return moment(this.task.finished_at, moment.ISO_8601).format(
        DateFormatEnum.DISPLAY_DATE
      );
    },
    displayActiveTasks() {
      return this.interval >= 0;
    },
    momentDate() {
      return moment(this.task.date, moment.ISO_8601);
    },
    dateHasPassed() {
      if (this.momentDate.isValid()) {
        return this.momentDate.isBefore();
      }

      return false;
    },
    dateColor() {
      return this.dateHasPassed ? "#ff0052" : "#a465a9";
    },
    hasProductLabels() {
      return (
        typeof this.task.product_labels !== "undefined" &&
        this.task.product_labels !== null &&
        typeof this.task.product_labels.data !== "undefined" &&
        this.task.product_labels.data !== null
      );
    },
    productLabels() {
      return this.hasProductLabels ? this.task.product_labels.data : [];
    },
    canDeleteTask() {
      return this.$store.state.permissions.delete_task === true;
    },
    displayTaskListDrawerButton() {
      return this.task.task_list_id && this.$store.state.permissions.task_list === true;
    },
  },
  methods: {
    onUnmatchClick() {
      this.unmatchLoading = true;
      taskApi
        .unmatchTask(this.task.id)
        .then(() => {
          this.task.customer_id = null;
          this.unmatchLoading = false;
          this.$emit("onUnmatched", this.task);
        })
        .catch(() => {
          this.$notification["error"]({
            message: "Er ging iets mis",
            description: "Kon de taak niet ontkoppelen",
          });
        });
    },
    processBadges(badge, count) {
      let $badge = $(badge);
      if (count === 0) {
        $badge.addClass("hidden");
      } else {
        $badge.removeClass("hidden").text(count);
      }
    },
    onCompletedClick() {
      this.completeLoading = true;

      taskApi
        .completeTask(this.task.id, this.userId, this.interval)
        .then((response) => {
          this.$emit("onCompleted", this.task);
          this.completeLoading = false;
        })
        .catch(() => {
          this.$notification["error"]({
            message: "Er ging iets mis",
            description: "Kon de taak niet verwerken",
          });
        });
    },
    onActivateClick() {
      this.activateLoading = true;

      taskApi
        .activateTask(this.task.id, this.userId, this.interval)
        .then((response) => {
          this.$emit("onCompleted", this.task);
          this.activateLoading = false;
        })
        .catch(() => {
          this.$notification["error"]({
            message: "Er ging iets mis",
            description: "Kon de taak niet activeren",
          });
        });
    },
    onEditClick() {
      if (this.hasCustomer) {
        window.location.href =
          "/customer/" + this.customerId + "/task/" + this.task.id + "/edit";
      } else {
        window.location.href = "/task/" + this.task.id + "/edit";
      }
    },
    onEditNameClick(e) {
      this.editing = true;
    },
    onSave() {
      this.updateTaskName();
    },
    onCancel() {
      this.editing = false;
      this.$refs.taskNameInput.stateValue = this.task.name;
    },
    onAttachableDrawerOpen() {
      this.attachableDrawerVisible = true;
    },
    onAttachableDrawerClose() {
      this.attachableDrawerVisible = false;
    },
    updateTaskName() {
      if (this.task.content === this.$refs.taskNameInput.stateValue) {
        this.editing = false;
        return;
      }

      this.task.content = this.$refs.taskNameInput.stateValue;
      this.updateNameLoading = true;

      taskApi
        .updateName(this.task)
        .then((response) => {
          this.editing = false;
          this.updateNameLoading = false;
        })
        .catch((error) => {
          this.$notification["error"]({
            message: "Er ging iets mis",
            description: "Kon de gewijzigde naam van de taak niet opslaan",
          });
        });
    },
    onSelectionChanged(e) {
      if (!this.computedIsSelectable) {
        return false;
      }

      let shiftKey = false;

      if (typeof e.nativeEvent !== "undefined") {
        shiftKey = e.nativeEvent.shiftKey;
      }

      this.$emit("onSelectionChanged", this.task, !this.selected, shiftKey);
    },
    updateTaskDate(taskId, amount = 1, period = "days") {
      let currentTaskDate = this.task.date;
      this.updateDateLoading = true;

      let dateMutation = moment({
        hour: 0,
        minutes: 0,
      }).add(amount, period);
      this.task.date = moment(dateMutation, moment.ISO_8601).format(
        "YYYY-MM-DD"
      );

      taskApi
        .updateDate(this.task)
        .then((response) => {
          this.$emit("onDateChanged", this.task);
          this.$notification["success"]({
            message: "Succes",
            description: "Nieuwe datum voor de taak is opgeslagen",
          });
        })
        .catch((error) => {
          this.task.date = currentTaskDate;
          this.$notification["error"]({
            message: "Er ging iets mis",
            description: "Kon de nieuwe datum van de taak niet opslaan",
          });
        })
        .finally(() => {
          this.updateDateLoading = false;
        });
    },
    afterDelete() {
      this.deleteModalVisible = false;
      this.$emit("onDeleted", this.task.id);
    },
  },
};
</script>

<style scoped>
tr td.half-padding {
  padding: 4px;
}

tr:not(.selected):hover {
  background-color: #1890ff0a;
}

tr.selected {
  background-color: #1890ff1c;
}

table th.compact,
table td.compact {
  white-space: nowrap;
  width: 1px !important;
}

tr .pencil-icon.edit-name {
  display: none;
}

tr:hover .pencil-icon.edit-name {
  display: initial;
}
</style>
